import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminData } from '@/hooks/useAdminData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { IconButton } from '@/components/ui/icon-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Edit2, Search, Filter, CalendarIcon, ChevronUp, ChevronDown, Users, Shield, Link2, UserPlus } from 'lucide-react';
import { Doctor, Staff, DentalProcedure, AdminTask, Room } from '@/services/adminService';
import { format } from 'date-fns';
import DoctorPreferencesModal from './DoctorPreferencesModal';
import UserManagement from './admin/UserManagement';
import PlatoDoctorMappingManager from './plato/PlatoDoctorMappingManager';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import LoadingOverlay from './LoadingOverlay';

type EntityType = 'doctors' | 'staff' | 'procedures' | 'adminTasks' | 'rooms' | 'users' | 'platoSync';

interface FormData {
  doctors: Omit<Doctor, 'id' | 'created_at' | 'updated_at'>;
  staff: Omit<Staff, 'id' | 'created_at' | 'updated_at'>;
  procedures: Omit<DentalProcedure, 'id' | 'created_at' | 'updated_at'>;
  adminTasks: Omit<AdminTask, 'id' | 'created_at' | 'updated_at'>;
  rooms: Omit<Room, 'id' | 'created_at' | 'updated_at'>;
}

interface AdminViewProps {
  initialTab?: string;
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
}

const AdminView = ({ initialTab, isLoadingSchedules = false, isFetchingSchedules = false }: AdminViewProps) => {
  const navigate = useNavigate();
  const {
    doctors,
    staff,
    procedures,
    adminTasks,
    rooms,
    createDoctor,
    updateDoctor,
    createStaff,
    updateStaff,
    createProcedure,
    updateProcedure,
    createAdminTask,
    updateAdminTask,
    createRoom,
    updateRoom,
  } = useAdminData();

  const { canManageUsers } = useAuth();
  const { currentClinic } = useClinic();
  const [isTabLoading, setIsTabLoading] = useState(false);

  // Check if current clinic is Forum (for Plato sync)
  const isForumClinic = currentClinic?.code?.toLowerCase() === 'forum';

  // Helper function to get singular form of entity names
  const getEntitySingular = (entityType: EntityType): string => {
    const singularMap: Record<EntityType, string> = {
      'doctors': 'Doctor',
      'staff': 'Staff',
      'procedures': 'Procedure',
      'adminTasks': 'Admin Task',
      'rooms': 'Room',
      'users': 'User',
      'platoSync': 'Plato Sync'
    };
    return singularMap[entityType] || entityType.slice(0, -1);
  };

  // Map URL-friendly tab names to actual tab values
  const tabMapping: { [key: string]: EntityType } = useMemo(() => ({
    'doctors': 'doctors',
    'staff': 'staff',
    'procedures': 'procedures',
    'admin-tasks': 'adminTasks',
    'rooms': 'rooms',
    'users': 'users',
    'plato-sync': 'platoSync'
  }), []);

  // Determine the default tab from the URL or props
  const getDefaultTab = useCallback((): EntityType => {
    if (initialTab && tabMapping[initialTab]) {
      return tabMapping[initialTab];
    }
    return 'doctors';
  }, [initialTab, tabMapping]);

  const [activeTab, setActiveTab] = useState<EntityType>(getDefaultTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Update tab when URL changes
  useEffect(() => {
    if (initialTab && tabMapping[initialTab]) {
      setActiveTab(tabMapping[initialTab]);
    }
  }, [initialTab, tabMapping]);
  
  // Handle tab change
  const handleTabChange = (value: string) => {
    setIsTabLoading(true);
    setActiveTab(value as EntityType);
    // Update URL with the new tab
    const reverseMapping: { [key: string]: string } = {
      'doctors': 'doctors',
      'staff': 'staff',
      'procedures': 'procedures',
      'adminTasks': 'admin-tasks',
      'rooms': 'rooms',
      'users': 'users',
      'platoSync': 'plato-sync',
    };
    navigate(`/admin/${reverseMapping[value]}`);

    // Clear loading after minimum display time
    setTimeout(() => setIsTabLoading(false), 300);
  };
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Doctor | Staff | DentalProcedure | AdminTask | Room | null>(null);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [selectedDoctorForPreferences, setSelectedDoctorForPreferences] = useState<Doctor | null>(null);

  const [formData, setFormData] = useState<FormData>({
    doctors: { name: '', short_name: '', specialization: '', color: '#3B82F6', status: 'active', company: 'ENDO', priority: 0, join_date: undefined, preferred_room_id: undefined },
    staff: { name: '', short_name: '', role: '', color: '#10B981', status: 'active', priority: 0, join_date: undefined },
    procedures: { name: '', description: '', duration: 30, estimated_duration: 30, category: '', price: 0, status: 'active' },
    adminTasks: { name: '', description: '', duration: 15, estimated_duration: 15, category: '', priority: 'medium', status: 'active' },
    rooms: { name: '', clinic: '', clinic_id: '', color: '#CBD5E0', status: 'active' },
  });

  const resetForm = () => {
    setFormData({
      doctors: { name: '', short_name: '', specialization: '', color: '#3B82F6', status: 'active', company: 'ENDO', priority: 0, join_date: undefined, preferred_room_id: undefined },
      staff: { name: '', short_name: '', role: '', color: '#10B981', status: 'active', priority: 0, join_date: undefined },
      procedures: { name: '', description: '', duration: 30, estimated_duration: 30, category: '', price: 0, status: 'active' },
      adminTasks: { name: '', description: '', duration: 15, estimated_duration: 15, category: '', priority: 'medium', status: 'active' },
      rooms: { name: '', clinic: '', clinic_id: '', color: '#CBD5E0', status: 'active' },
    });
    setEditingItem(null);
  };

  const openModal = (type: EntityType, item?: Doctor | Staff | DentalProcedure | AdminTask | Room) => {
    setActiveTab(type);
    if (item) {
      setEditingItem(item);
      setFormData(prev => ({
        ...prev,
        [type]: { ...item }
      }));
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (type: EntityType) => {
    const data = formData[type];
    
    if (editingItem) {
      switch (type) {
        case 'doctors':
          updateDoctor({ id: editingItem.id, updates: data as Partial<Doctor> });
          break;
        case 'staff':
          updateStaff({ id: editingItem.id, updates: data as Partial<Staff> });
          break;
        case 'procedures':
          updateProcedure({ id: editingItem.id, updates: data as Partial<DentalProcedure> });
          break;
        case 'adminTasks':
          updateAdminTask({ id: editingItem.id, updates: data as Partial<AdminTask> });
          break;
        case 'rooms':
          updateRoom({ id: editingItem.id, updates: data as Partial<Room> });
          break;
      }
    } else {
      switch (type) {
        case 'doctors':
          createDoctor(data as Omit<Doctor, 'id' | 'created_at' | 'updated_at'>);
          break;
        case 'staff':
          createStaff(data as Omit<Staff, 'id' | 'created_at' | 'updated_at'>);
          break;
        case 'procedures':
          createProcedure(data as Omit<DentalProcedure, 'id' | 'created_at' | 'updated_at'>);
          break;
        case 'adminTasks':
          createAdminTask(data as Omit<AdminTask, 'id' | 'created_at' | 'updated_at'>);
          break;
        case 'rooms':
          createRoom(data as Omit<Room, 'id' | 'created_at' | 'updated_at'>);
          break;
      }
    }
    
    setIsModalOpen(false);
    resetForm();
  };

  const toggleStatus = (type: EntityType, item: Doctor | Staff | DentalProcedure | AdminTask | Room) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    switch (type) {
      case 'doctors':
        updateDoctor({ id: item.id, updates: { status: newStatus } });
        break;
      case 'staff':
        updateStaff({ id: item.id, updates: { status: newStatus } });
        break;
      case 'procedures':
        updateProcedure({ id: item.id, updates: { status: newStatus } });
        break;
      case 'adminTasks':
        updateAdminTask({ id: item.id, updates: { status: newStatus } });
        break;
      case 'rooms':
        updateRoom({ id: item.id, updates: { status: newStatus } });
        break;
    }
  };

  const openPreferencesModal = (doctor: Doctor) => {
    setSelectedDoctorForPreferences(doctor);
    setIsPreferencesModalOpen(true);
  };

  const closePreferencesModal = () => {
    setIsPreferencesModalOpen(false);
    setSelectedDoctorForPreferences(null);
  };

  // Handle three-state sorting: asc -> desc -> null
  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortBy(null);
        setSortOrder(null);
      }
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getFilteredAndSortedData = (data: (Doctor | Staff | DentalProcedure | AdminTask | Room)[], type: EntityType) => {
    const filtered = data.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    // Sort the data
    filtered.sort((a, b) => {
      // First, always sort by status - active items come first
      if (a.status !== b.status) {
        return a.status === 'active' ? -1 : 1;
      }

      // If both have the same status and we have sorting criteria, apply it
      if (sortBy && sortOrder) {
        let aValue: string | number = '';
        let bValue: string | number = '';
        
        // Safe property access using bracket notation with unknown cast first
        const aProp = (a as unknown as Record<string, unknown>)[sortBy];
        const bProp = (b as unknown as Record<string, unknown>)[sortBy];
        
        aValue = aProp !== undefined ? String(aProp) : '';
        bValue = bProp !== undefined ? String(bProp) : '';
        
        if (typeof aProp === 'number' && typeof bProp === 'number') {
          aValue = aProp;
          bValue = bProp;
        } else {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      // If no sorting criteria or same status and values, maintain original order
      return 0;
    });

    return filtered;
  };

  const renderDoctorsTable = () => {
    const filteredDoctors = getFilteredAndSortedData(doctors, 'doctors') as Doctor[];
    
    return (
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  Name
                  {sortBy === 'name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('short_name')}>
                <div className="flex items-center gap-1">
                  Short Name
                  {sortBy === 'short_name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('company')}>
                <div className="flex items-center gap-1">
                  Company
                  {sortBy === 'company' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('priority')}>
                <div className="flex items-center gap-1">
                  Priority
                  {sortBy === 'priority' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('specialization')}>
                <div className="flex items-center gap-1">
                  Specialization
                  {sortBy === 'specialization' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Color</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('join_date')}>
                <div className="flex items-center gap-1">
                  Join Date
                  {sortBy === 'join_date' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDoctors.map((doctor) => (
              <TableRow key={doctor.id} className="table-row-hover">
                <TableCell>{doctor.name}</TableCell>
                <TableCell>{doctor.short_name || 'N/A'}</TableCell>
                <TableCell>{doctor.company || 'N/A'}</TableCell>
                <TableCell>{doctor.priority}</TableCell>
                <TableCell>{doctor.specialization}</TableCell>
                <TableCell>
                  <div 
                    className="w-6 h-6 rounded-full border border-gray-300" 
                    style={{ backgroundColor: doctor.color }}
                  />
                </TableCell>
                <TableCell>
                  {doctor.join_date ? format(new Date(doctor.join_date), 'MMM dd, yyyy') : 'N/A'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={doctor.status} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal('doctors', doctor)}
                      icon={Edit2}
                      srLabel="Edit doctor"
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreferencesModal(doctor)}
                      icon={Users}
                      srLabel="Manage staff preferences"
                    />
                    <Button
                      variant={doctor.status === 'active' ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus('doctors', doctor)}
                    >
                      {doctor.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderStaffTable = () => {
    const filteredStaff = getFilteredAndSortedData(staff, 'staff') as Staff[];
    
    return (
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  Name
                  {sortBy === 'name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('short_name')}>
                <div className="flex items-center gap-1">
                  Short Name
                  {sortBy === 'short_name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('role')}>
                <div className="flex items-center gap-1">
                  Role
                  {sortBy === 'role' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('priority')}>
                <div className="flex items-center gap-1">
                  Priority
                  {sortBy === 'priority' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Color</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('join_date')}>
                <div className="flex items-center gap-1">
                  Join Date
                  {sortBy === 'join_date' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStaff.map((staffMember) => (
              <TableRow key={staffMember.id} className="table-row-hover">
                <TableCell>{staffMember.name}</TableCell>
                <TableCell>{staffMember.short_name || 'N/A'}</TableCell>
                <TableCell>{staffMember.role}</TableCell>
                <TableCell>{staffMember.priority}</TableCell>
                <TableCell>
                  <div 
                    className="w-6 h-6 rounded-full border border-gray-300" 
                    style={{ backgroundColor: staffMember.color }}
                  />
                </TableCell>
                <TableCell>
                  {staffMember.join_date ? format(new Date(staffMember.join_date), 'MMM dd, yyyy') : 'N/A'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={staffMember.status} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal('staff', staffMember)}
                      icon={Edit2}
                      srLabel="Edit staff member"
                    />
                    <Button
                      variant={staffMember.status === 'active' ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus('staff', staffMember)}
                    >
                      {staffMember.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderProceduresTable = () => {
    const filteredProcedures = getFilteredAndSortedData(procedures, 'procedures') as DentalProcedure[];
    
    return (
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  Name
                  {sortBy === 'name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
                <div className="flex items-center gap-1">
                  Description
                  {sortBy === 'description' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('category')}>
                <div className="flex items-center gap-1">
                  Category
                  {sortBy === 'category' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('duration')}>
                <div className="flex items-center gap-1">
                  Duration
                  {sortBy === 'duration' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('price')}>
                <div className="flex items-center gap-1">
                  Price
                  {sortBy === 'price' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProcedures.map((procedure) => (
              <TableRow key={procedure.id} className="table-row-hover">
                <TableCell>{procedure.name}</TableCell>
                <TableCell>{procedure.description}</TableCell>
                <TableCell>{procedure.category}</TableCell>
                <TableCell>{procedure.duration} min</TableCell>
                <TableCell>${procedure.price.toFixed(2)}</TableCell>
                <TableCell>
                  <StatusBadge status={procedure.status} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal('procedures', procedure)}
                      icon={Edit2}
                      srLabel="Edit procedure"
                    />
                    <Button
                      variant={procedure.status === 'active' ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus('procedures', procedure)}
                    >
                      {procedure.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderAdminTasksTable = () => {
    const filteredAdminTasks = getFilteredAndSortedData(adminTasks, 'adminTasks') as AdminTask[];
    
    return (
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  Name
                  {sortBy === 'name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
                <div className="flex items-center gap-1">
                  Description
                  {sortBy === 'description' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('category')}>
                <div className="flex items-center gap-1">
                  Category
                  {sortBy === 'category' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('duration')}>
                <div className="flex items-center gap-1">
                  Duration
                  {sortBy === 'duration' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('priority')}>
                <div className="flex items-center gap-1">
                  Priority
                  {sortBy === 'priority' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdminTasks.map((task) => (
              <TableRow key={task.id} className="table-row-hover">
                <TableCell>{task.name}</TableCell>
                <TableCell>{task.description}</TableCell>
                <TableCell>{task.category}</TableCell>
                <TableCell>{task.duration} min</TableCell>
                <TableCell>
                  <StatusBadge 
                    status={
                      task.priority === 'high' ? 'overdue' : 
                      task.priority === 'medium' ? 'pending' : 'inactive'
                    }
                  >
                    {task.priority}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <StatusBadge status={task.status} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal('adminTasks', task)}
                      icon={Edit2}
                      srLabel="Edit admin task"
                    />
                    <Button
                      variant={task.status === 'active' ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus('adminTasks', task)}
                    >
                      {task.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderRoomsTable = () => {
    const filteredRooms = getFilteredAndSortedData(rooms, 'rooms') as Room[];
    
    return (
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  Name
                  {sortBy === 'name' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('clinic')}>
                <div className="flex items-center gap-1">
                  Clinic
                  {sortBy === 'clinic' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && sortOrder && (
                    sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRooms.map((room) => (
              <TableRow key={room.id} className="table-row-hover">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border border-gray-300" 
                      style={{ backgroundColor: room.color || '#CBD5E0' }}
                      title={room.color || '#CBD5E0'}
                    />
                    {room.name}
                  </div>
                </TableCell>
                <TableCell>{room.clinic}</TableCell>
                <TableCell>
                  <StatusBadge status={room.status} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal('rooms', room)}
                      icon={Edit2}
                      srLabel="Edit room"
                    />
                    <Button
                      variant={room.status === 'active' ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus('rooms', room)}
                    >
                      {room.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderModalContent = () => {
    return (
      <div className="space-y-4">
        {activeTab === 'doctors' && (
          <>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.doctors.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="short_name">Short Name</Label>
              <Input
                id="short_name"
                value={formData.doctors.short_name || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, short_name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="specialization">Specialization</Label>
              <Input
                id="specialization"
                value={formData.doctors.specialization}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, specialization: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="company">Company</Label>
              <Select 
                value={formData.doctors.company || 'ENDO'}
                onValueChange={(value: string) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, company: value }
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENDO">ENDO</SelectItem>
                  <SelectItem value="TDS">TDS</SelectItem>
                  <SelectItem value="SSD">SSD</SelectItem>
                  <SelectItem value="TCD">TCD</SelectItem>
                  <SelectItem value="EM">EM</SelectItem>
                  <SelectItem value="TCEP">TCEP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                value={formData.doctors.priority}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, priority: parseInt(e.target.value) }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="join_date">Join Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.doctors.join_date ? format(new Date(formData.doctors.join_date), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={5}>
                  <div className="p-4">
                    <Calendar
                      mode="single"
                      selected={formData.doctors.join_date ? new Date(formData.doctors.join_date) : undefined}
                      onSelect={(date) => setFormData(prev => ({
                        ...prev,
                        doctors: { ...prev.doctors, join_date: date ? format(date, 'yyyy-MM-dd') : undefined }
                      }))}
                      initialFocus
                      captionLayout="dropdown-buttons"
                      fromYear={2019}
                      toYear={new Date().getFullYear() + 1}
                      className="rounded-md border"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="preferred_room">Preferred Room</Label>
              <Select
                value={formData.doctors.preferred_room_id || 'none'}
                onValueChange={(value: string) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, preferred_room_id: value === 'none' ? undefined : value }
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select preferred room" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {rooms
                    .filter(r => r.status === 'active')
                    .map(room => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.name} - {room.clinic}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                type="color"
                value={formData.doctors.color}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  doctors: { ...prev.doctors, color: e.target.value }
                }))}
              />
            </div>
          </>
        )}

        {activeTab === 'staff' && (
          <>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.staff.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  staff: { ...prev.staff, name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="short_name">Short Name</Label>
              <Input
                id="short_name"
                value={formData.staff.short_name || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  staff: { ...prev.staff, short_name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={formData.staff.role}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  staff: { ...prev.staff, role: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                value={formData.staff.priority}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  staff: { ...prev.staff, priority: parseInt(e.target.value) }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="join_date">Join Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.staff.join_date ? format(new Date(formData.staff.join_date), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={5}>
                  <div className="p-4">
                    <Calendar
                      mode="single"
                      selected={formData.staff.join_date ? new Date(formData.staff.join_date) : undefined}
                      onSelect={(date) => setFormData(prev => ({
                        ...prev,
                        staff: { ...prev.staff, join_date: date ? format(date, 'yyyy-MM-dd') : undefined }
                      }))}
                      initialFocus
                      captionLayout="dropdown-buttons"
                      fromYear={2019}
                      toYear={new Date().getFullYear() + 1}
                      className="rounded-md border"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                type="color"
                value={formData.staff.color}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  staff: { ...prev.staff, color: e.target.value }
                }))}
              />
            </div>
          </>
        )}

        {activeTab === 'procedures' && (
          <>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.procedures.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.procedures.description}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, description: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.procedures.category}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, category: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={formData.procedures.duration}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, duration: parseInt(e.target.value) }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="estimated_duration">Estimated Duration (minutes)</Label>
              <Input
                id="estimated_duration"
                type="number"
                value={formData.procedures.estimated_duration}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, estimated_duration: parseInt(e.target.value) }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.procedures.price}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  procedures: { ...prev.procedures, price: parseFloat(e.target.value) }
                }))}
              />
            </div>
          </>
        )}

        {activeTab === 'adminTasks' && (
          <>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.adminTasks.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  adminTasks: { ...prev.adminTasks, name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.adminTasks.description}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  adminTasks: { ...prev.adminTasks, description: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.adminTasks.category}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  adminTasks: { ...prev.adminTasks, category: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={formData.adminTasks.duration}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  adminTasks: { ...prev.adminTasks, duration: parseInt(e.target.value) }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select 
                value={formData.adminTasks.priority}
                onValueChange={(value: 'low' | 'medium' | 'high') => setFormData(prev => ({
                  ...prev,
                  adminTasks: { ...prev.adminTasks, priority: value }
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {activeTab === 'rooms' && (
          <>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.rooms.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  rooms: { ...prev.rooms, name: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="clinic">Clinic</Label>
              <Input
                id="clinic"
                value={formData.rooms.clinic}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  rooms: { ...prev.rooms, clinic: e.target.value }
                }))}
              />
            </div>
            <div>
              <Label htmlFor="color">Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="color"
                  type="color"
                  value={formData.rooms.color || '#CBD5E0'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    rooms: { ...prev.rooms, color: e.target.value }
                  }))}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  value={formData.rooms.color || '#CBD5E0'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    rooms: { ...prev.rooms, color: e.target.value }
                  }))}
                  placeholder="#CBD5E0"
                  className="flex-1"
                />
              </div>
            </div>
          </>
        )}

        <div>
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData[activeTab].status}
            onValueChange={(value: 'active' | 'inactive') => setFormData(prev => ({
              ...prev,
              [activeTab]: { ...prev[activeTab], status: value }
            }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => handleSubmit(activeTab)}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold">Administration</h1>
        {canManageUsers() && (
          // Secondary entry point. Primary lives at /patients/referring-dentists
          // (the "Referring dentists" tab inside the Patients view). Kept here
          // as a deep-link fallback for admin workflows.
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/referring-dentists')}
            className="self-start sm:self-auto text-slate-600 hover:text-slate-900"
            title="Deep link to the referring-dentist directory. The primary entry point is now under Patients."
          >
            <UserPlus className="h-4 w-4 mr-1.5" />
            Referring Dentists
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="px-3 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <CardTitle>Management</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 shrink-0" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 shrink-0" />
                <Select value={statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive') => setStatusFilter(value)}>
                  <SelectTrigger className="w-full sm:w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
              <div className="overflow-x-auto -mx-1 px-1 pb-1">
                <TabsList className="w-fit">
                  <TabsTrigger value="doctors" className="text-xs sm:text-sm px-2 sm:px-3">Doctors</TabsTrigger>
                  <TabsTrigger value="staff" className="text-xs sm:text-sm px-2 sm:px-3">Staff</TabsTrigger>
                  <TabsTrigger value="procedures" className="text-xs sm:text-sm px-2 sm:px-3">Procedures</TabsTrigger>
                  <TabsTrigger value="adminTasks" className="text-xs sm:text-sm px-2 sm:px-3">Tasks</TabsTrigger>
                  <TabsTrigger value="rooms" className="text-xs sm:text-sm px-2 sm:px-3">Rooms</TabsTrigger>
                  {canManageUsers() && (
                    <TabsTrigger value="users" className="text-xs sm:text-sm px-2 sm:px-3">
                      <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                      Users
                    </TabsTrigger>
                  )}
                  {isForumClinic && (
                    <TabsTrigger value="platoSync" className="text-xs sm:text-sm px-2 sm:px-3">
                      <Link2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                      Plato
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {activeTab !== 'users' && activeTab !== 'platoSync' && (
                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => openModal(activeTab)} size="sm">
                      <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Add {getEntitySingular(activeTab)}</span>
                      <span className="sm:hidden">Add</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingItem ? 'Edit' : 'Add'} {getEntitySingular(activeTab)}
                      </DialogTitle>
                    </DialogHeader>
                    {renderModalContent()}
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <TabsContent value="doctors">
              {renderDoctorsTable()}
            </TabsContent>
            <TabsContent value="staff">
              {renderStaffTable()}
            </TabsContent>
            <TabsContent value="procedures">
              {renderProceduresTable()}
            </TabsContent>
            <TabsContent value="adminTasks">
              {renderAdminTasksTable()}
            </TabsContent>
            <TabsContent value="rooms">
              {renderRoomsTable()}
            </TabsContent>
            {canManageUsers() && (
              <TabsContent value="users">
                <UserManagement
                  searchTerm={searchTerm}
                  statusFilter={statusFilter}
                />
              </TabsContent>
            )}
            {isForumClinic && currentClinic && (
              <TabsContent value="platoSync">
                <PlatoDoctorMappingManager clinicId={currentClinic.id} />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Doctor Preferences Modal */}
      {selectedDoctorForPreferences && (
        <DoctorPreferencesModal
          isOpen={isPreferencesModalOpen}
          onClose={closePreferencesModal}
          doctor={selectedDoctorForPreferences}
          staff={staff || []}
          onPreferencesUpdated={() => {
            // Preferences updated, could trigger a refresh if needed
            console.log('Doctor preferences updated');
          }}
        />
      )}

      <LoadingOverlay
        isLoading={isLoadingSchedules || isTabLoading}
        message={isTabLoading ? "Switching tab..." : "Loading admin data..."}
      />
    </div>
  );
};

export default AdminView;
